import React, { useContext, useEffect, useState } from 'react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useLocalObservable } from 'mobx-react-lite';
import { CustomSpinner } from '@yakoffice/custom-spinner';
import { EntityStatus } from '@yakoffice/publisher-types';
import RootStoreContext from '../../../model/RootStoreContext';
import type { IGameEnvironment } from '../../../model/gameEnvironment/GameEnvironment';
import type { IEntityVersion } from '../../../model/entity/EntityVersion';
import { IGameVersion } from '../../../model/gameVersion/GameVersion';
import { useEntitiesController } from '../../../controllers/useEntitiesController';
import { useGoogleSheetExists } from '../../../loaders/useGoogleSheetExists';
import { useGameEnvironmentCssClass } from '../common/useGameEnvironmentCssClass';
import { EntitiesNav } from './EntitiesNav';
import { EntitiesTable } from './EntitiesTable';
import { EntitiesPerPageKey, useEntitiesSearch } from './useEntitiesSearch';
import { YakofficePageSize } from '../../components/yakoffice-pagesize/YakofficePageSize';
import { YakofficePagination } from '../../components/yakoffice-pagination/YakofficePagination';
import { usePageTitle } from '../pageTitleContext/PageTitleContext';


export interface ICheckedEntity {
    entityVersion: IEntityVersion;
    checked: boolean;
}

export const Entities = observer(() => {

    const rootStore = useContext(RootStoreContext);
    const cssClass = useGameEnvironmentCssClass();
    const [refreshToggle, setRefreshToggle] = useState(true);
    const { setPageTitle } = usePageTitle();

    const {
        query,
        loadEntitiesResult,
        entitiesCountResult,
        setPageSize,
        setPageNumber
    } = useEntitiesSearch({
        defaultPageSize: parseInt(window.localStorage.getItem(EntitiesPerPageKey) ?? '100'),
        refresh: refreshToggle
    });

    const googleSheetExistsResult = useGoogleSheetExists();
    const controller = useEntitiesController();
    const localStore = useLocalObservable(() => ({ checkedEntities: [] as ICheckedEntity[] }));
    const statusStore = useLocalObservable(() => ({
        statusFilters: [EntityStatus.Draft, EntityStatus.Stopped, EntityStatus.Published] as string[]
    }));

    useEffect(() => {
        const title = `${rootStore.kindVersionStore.currentKindVersion?.name}` || 'Kind';
        setPageTitle([title, 'Configurations']);
        runInAction(() => localStore.checkedEntities = loadEntitiesResult.entityVersions.map(entity => ({
            entityVersion: entity,
            checked: false
        })));
    }, [loadEntitiesResult.entityVersions, localStore]);

    const handleCopyEntities = (targetGameVersions: IGameVersion[], targetGameEnvironments: IGameEnvironment[]) => {
        controller.handleCopyEntities(localStore.checkedEntities.filter(entity => entity.checked).map(_entity => _entity.entityVersion), targetGameVersions, targetGameEnvironments);
    };

    const handleChangePageNumber = (pageNumber: number) => {
        setPageNumber(pageNumber);
    };

    const handleDownloadAsJson = () => {

        const checkedEntities = localStore.checkedEntities.filter(entity => entity.checked);

        const entitiesToDownload = (checkedEntities.length > 0 ? checkedEntities : localStore.checkedEntities).map(_entity => _entity.entityVersion.entity.id);

        controller.handleDownloadAsJson(entitiesToDownload);
    };

    const handleBatchStatusChange = async (status: EntityStatus) => {
        controller.batchStatusUpdate(
            localStore.checkedEntities.filter(entity => entity.checked).map(_entity => _entity.entityVersion),
            status,
            () => setRefreshToggle(!refreshToggle));
    };

    const handleBatchUpdate = () => {
        controller.batchUpdate(
            localStore.checkedEntities.filter(entity => entity.checked).map(_entity => _entity.entityVersion),
            () => setRefreshToggle(!refreshToggle));
    };

    const handleCreateSheet = () => {
        controller.handleCreateSheet(statusStore.statusFilters);
    };

    const handleRefreshSheet = () => {
        controller.handleRefreshSheet(statusStore.statusFilters);
    };

    const handleUploadSheet = () => {
        controller.handleUploadSheet(() => setRefreshToggle(!refreshToggle));
    };

    const handleBatchDelete = () => {
        controller.batchDelete(
            localStore.checkedEntities.filter(entity => entity.checked).map(_entity => _entity.entityVersion),
            () => setRefreshToggle(!refreshToggle));
    };

    return (
        rootStore.kindVersionStore.currentKindVersion &&
        <div id="entities" className="h-100">
            {!loadEntitiesResult.isLoaded || !googleSheetExistsResult.isLoaded
                ? <CustomSpinner spinnerText="Loading Entities..." />
                : <>
                    <EntitiesNav
                        googleSheetExistsResult={googleSheetExistsResult}
                        handleBatchCopy={handleCopyEntities}
                        handleBatchStatusUpdate={handleBatchStatusChange}
                        handleBatchUpdate={handleBatchUpdate}
                        handleBatchDelete={handleBatchDelete}
                        handleCreateSheet={handleCreateSheet}
                        handleViewSheet={controller.handleViewSheet}
                        handleUploadSheet={handleUploadSheet}
                        handleRefreshSheet={handleRefreshSheet}
                        handleDeleteSheet={controller.handleDeleteSheet}
                        handleDownloadAsJson={handleDownloadAsJson}
                        checkedEntityVersions={localStore.checkedEntities}
                        statusStore={statusStore}
                        singleEntityView={localStore.checkedEntities.filter(e => statusStore.statusFilters.includes(e.entityVersion.entity.status)).length < 2}
                        isStaticData={rootStore.kindVersionStore.currentKindVersion.isStaticData}
                    />

                    <EntitiesTable
                        checkedEntityVersions={localStore.checkedEntities.filter(e => statusStore.statusFilters.includes(e.entityVersion.entity.status))}
                        cssColorClass={cssClass.name} statusStore={statusStore}
                        pagination={<>{query.pageSize &&
                            <span className="">Rows per page <YakofficePageSize pageSize={query.pageSize}
                                                                                handleSetPageSize={setPageSize} />
                        </span>
                        }
                            {(query.pageSize && query.pageNumber && !!entitiesCountResult.count) && (query.pageSize < entitiesCountResult.count) &&
                                <YakofficePagination pageSize={query.pageSize}
                                                     activePageNumber={query.pageNumber}
                                                     itemsCount={entitiesCountResult.count}
                                                     handleSetPageNumber={handleChangePageNumber} />
                            }</>}

                    />
                </>
            }
        </div>
    );
});
